import React, { useEffect, useMemo, useState } from "react";
import ContactSearchInput from "@/components/ContactSearchInput";
import { AutomationStoppedPill } from "@/components/AutomationStoppedPill";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  KanbanSquare, Plus, Loader2, Pencil, Trash2, Trophy, X, ArrowRight,
  Calendar, Search, Plane, TrendingUp, BarChart3, Link2, Unlink, Sparkles,
  Clock, Settings, ExternalLink, RefreshCw, Cable, Power, PowerOff,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR, fmtDate } from "@/pages/trips/shared";
import CualificacionBoard from "@/pages/pipeline/CualificacionBoard";
import { useCrmLabels, fmtReminder } from "@/pages/pipeline/crmConfig";
import { StageLabelsDialog, GoogleConfigDialog, GcalConnect, ReminderDialog } from "@/pages/pipeline/PipelineDialogs";

const ADMIN_ROLES = new Set(["director_sales", "dir_operaciones", "socio_director", "dir_admin_general"]);
const COLS = ["interesado", "posible_cierre", "ganado", "perdido"];
const SOURCES = ["KimKim", "Zicasso", "Responsible Travel", "Fora", "Directo", "Otro"];
const ACCENT = {
  interesado: { bar: "bg-sky-500", dot: "bg-sky-500", text: "text-sky-700", chip: "border-sky-200 bg-sky-50 text-sky-700" },
  posible_cierre: { bar: "bg-amber-500", dot: "bg-amber-500", text: "text-amber-700", chip: "border-amber-200 bg-amber-50 text-amber-700" },
  ganado: { bar: "bg-emerald-500", dot: "bg-emerald-500", text: "text-emerald-700", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  perdido: { bar: "bg-red-500", dot: "bg-red-500", text: "text-red-700", chip: "border-red-200 bg-red-50 text-red-700" },
};

// ============================= Tarjeta =============================
function OpportunityCard({ op, isAdmin, labels, onEdit, onWin, onRemind, onDragStart, onDragEnd, isDragging }) {
  const qc = useQueryClient();
  const moveMut = useMutation({
    mutationFn: async (status) => (await api.patch(`/pipeline/opportunities/${op.id}/status`, { status })).data,
    onSuccess: (_, status) => { toast.success(`Movido a ${labels[status]}`); qc.invalidateQueries({ queryKey: ["pipeline-ops"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/pipeline/opportunities/${op.id}`)).data,
    onSuccess: () => { toast.success("Oportunidad eliminada"); qc.invalidateQueries({ queryKey: ["pipeline-ops"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const a = ACCENT[op.status];
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart?.(); }}
      onDragEnd={() => onDragEnd?.()}
      className={`group cursor-grab overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing ${isDragging ? "opacity-40 ring-2 ring-primary" : ""}`}
      data-testid={`pipeline-card-${op.id}`}>
      <div className={`h-1 w-full ${a.bar}`} />
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="font-heading text-sm font-semibold leading-tight">{op.client_name}</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded p-1 text-muted-foreground hover:bg-muted" data-testid={`pipeline-card-menu-${op.id}`} aria-label="Acciones"><ArrowRight className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onClick={() => onRemind(op)} data-testid={`pipeline-remind-${op.id}`}><Clock className="mr-2 h-3.5 w-3.5" />Agendar recordatorio…</DropdownMenuItem>
              <DropdownMenuSeparator />
              {op.status !== "interesado" && <DropdownMenuItem onClick={() => moveMut.mutate("interesado")} data-testid={`pipeline-move-interesado-${op.id}`}><ArrowRight className="mr-2 h-3.5 w-3.5" />Mover a {labels.interesado}</DropdownMenuItem>}
              {op.status !== "posible_cierre" && <DropdownMenuItem onClick={() => moveMut.mutate("posible_cierre")} data-testid={`pipeline-move-posible-${op.id}`}><ArrowRight className="mr-2 h-3.5 w-3.5" />Mover a {labels.posible_cierre}</DropdownMenuItem>}
              <DropdownMenuSeparator />
              {op.status !== "ganado" && <DropdownMenuItem onClick={() => onWin(op)} className="text-emerald-700" data-testid={`pipeline-win-${op.id}`}><Trophy className="mr-2 h-3.5 w-3.5" />Marcar {labels.ganado}…</DropdownMenuItem>}
              {op.status !== "perdido" && <DropdownMenuItem onClick={() => moveMut.mutate("perdido")} className="text-red-600" data-testid={`pipeline-move-perdido-${op.id}`}><X className="mr-2 h-3.5 w-3.5" />Marcar {labels.perdido}</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onEdit(op)} data-testid={`pipeline-edit-${op.id}`}><Pencil className="mr-2 h-3.5 w-3.5" />Editar</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { if (window.confirm("¿Eliminar esta oportunidad?")) delMut.mutate(); }} className="text-red-600" data-testid={`pipeline-del-${op.id}`}><Trash2 className="mr-2 h-3.5 w-3.5" />Eliminar</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-2 flex items-baseline justify-between gap-2">
          <span className="font-heading text-xl font-bold tabular-nums tracking-tight">{fmtEUR(op.amount)}</span>
          {op.arrival_date && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Calendar className="h-3 w-3" />{fmtDate(op.arrival_date)}</span>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {op.source && <Badge variant="outline" className="text-[10px]">{op.source}</Badge>}
          {op.source_external === "pipeline_legacy" && (
            <Badge variant="outline" className="border-violet-200 bg-violet-50 text-[10px] text-violet-700" title="Sincronizada desde app externa">
              <Cable className="mr-1 h-3 w-3" />Externa
            </Badge>
          )}
          {op.trip_id && <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700"><Plane className="mr-1 h-3 w-3" />Viaje</Badge>}
        </div>
        {op.next_action_at && (
          <button
            type="button"
            onClick={() => op.gcal_link ? window.open(op.gcal_link, "_blank") : onRemind(op)}
            className="mt-2 flex w-full items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-left text-[11px] text-indigo-700 transition-colors hover:bg-indigo-100"
            data-testid={`pipeline-nextaction-${op.id}`}>
            <Clock className="h-3 w-3 shrink-0" />
            <span className="truncate">{fmtReminder(op.next_action_at)}{op.next_action_note ? ` · ${op.next_action_note}` : ""}</span>
            {op.gcal_link && <ExternalLink className="ml-auto h-3 w-3 shrink-0" />}
          </button>
        )}
        {op.notes && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{op.notes}</p>}
        {isAdmin && <p className="mt-2.5 border-t pt-2 text-[11px] text-muted-foreground">Agente: <span className="font-medium text-foreground">{op.owner_name}</span></p>}
      </div>
    </div>
  );
}

// ============================= Diálogo crear/editar =============================
function OpportunityDialog({ open, onClose, initial, isAdmin, agents, labels }) {
  const qc = useQueryClient();
  const editing = !!initial;
  const [f, setF] = useState({});
  const [mode, setMode] = useState("lead"); // "lead" (desde existente) | "manual"
  useEffect(() => {
    if (open) {
      setMode("lead");
      setF(initial
        ? { ...initial }
        : { client_name: "", client_email: "", source: "", amount: "", arrival_date: "", notes: "", status: "interesado", owner_id: "" });
    }
  }, [open, initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => {
      const body = {
        client_name: f.client_name, client_email: f.client_email, source: f.source === "none" ? "" : f.source,
        amount: f.amount, arrival_date: f.arrival_date || null, notes: f.notes,
        ...(isAdmin && f.owner_id && f.owner_id !== "self" ? { owner_id: f.owner_id } : {}),
      };
      if (editing) return (await api.patch(`/pipeline/opportunities/${initial.id}`, body)).data;
      return (await api.post("/pipeline/opportunities", { ...body, status: f.status })).data;
    },
    onSuccess: () => { toast.success(editing ? "Oportunidad actualizada" : "Oportunidad creada"); qc.invalidateQueries({ queryKey: ["pipeline-ops"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pipeline-op-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KanbanSquare className="h-4 w-4 text-primary" />{editing ? "Editar oportunidad" : "Nueva oportunidad"}</DialogTitle>
          <DialogDescription className="text-xs">Sube el lead como potencial cliente. Se crea/actualiza su ficha sin duplicar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" data-testid="pipeline-mode-toggle">
              <button type="button" onClick={() => setMode("lead")} data-testid="pipeline-mode-lead"
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${mode === "lead" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                Desde lead existente
              </button>
              <button type="button" onClick={() => setMode("manual")} data-testid="pipeline-mode-manual"
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${mode === "manual" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                Nuevo (manual)
              </button>
            </div>
          )}
          <div>
            <Label className="text-xs">Cliente *</Label>
            {!editing && mode === "lead" ? (
              <ContactSearchInput
                value={f.client_name || ""}
                onChange={(v) => set("client_name", v)}
                onSelect={(c) => setF((s) => ({ ...s, client_name: c.name, client_email: c.email || s.client_email || "" }))}
                placeholder="Busca un lead/cliente existente…"
                testid="pipeline-f-name"
              />
            ) : (
              <Input value={f.client_name || ""} onChange={(e) => set("client_name", e.target.value)} placeholder="Nombre del potencial cliente" data-testid="pipeline-f-name" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email (opcional)</Label><Input type="email" value={f.client_email || ""} onChange={(e) => set("client_email", e.target.value)} placeholder="cliente@email.com" data-testid="pipeline-f-email" /></div>
            <div><Label className="text-xs">Importe estimado (€)</Label><Input type="number" step="0.01" min="0" value={f.amount ?? ""} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" data-testid="pipeline-f-amount" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fuente del lead</Label>
              <Select value={f.source || "none"} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="pipeline-f-source"><SelectValue placeholder="Fuente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin fuente —</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Fecha de llegada (tentativa)</Label><Input type="date" value={(f.arrival_date || "").slice(0, 10)} onChange={(e) => set("arrival_date", e.target.value)} data-testid="pipeline-f-arrival" /></div>
          </div>
          {isAdmin && (
            <div>
              <Label className="text-xs">Agente asignado</Label>
              <Select value={f.owner_id || "self"} onValueChange={(v) => set("owner_id", v)}>
                <SelectTrigger data-testid="pipeline-f-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">— Yo —</SelectItem>
                  {(agents || []).map((ag) => <SelectItem key={ag.id} value={ag.id}>{ag.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!editing && (
            <div>
              <Label className="text-xs">Estado inicial</Label>
              <Select value={f.status || "interesado"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="pipeline-f-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interesado">{labels.interesado}</SelectItem>
                  <SelectItem value="posible_cierre">{labels.posible_cierre}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Notas</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="pipeline-f-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!f.client_name || mut.isPending} data-testid="pipeline-f-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}{editing ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Diálogo ganar (asociar viaje) =============================
function WinDialog({ op, onClose }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const tripsQ = useQuery({
    queryKey: ["pipeline-match", op.id, q],
    queryFn: async () => (await api.get(`/pipeline/opportunities/${op.id}/match-trips`, { params: q ? { q } : {} })).data,
    enabled: !!op,
  });
  const trips = tripsQ.data?.items || [];
  const winMut = useMutation({
    mutationFn: async (trip_id) => (await api.post(`/pipeline/opportunities/${op.id}/win`, trip_id ? { trip_id } : {})).data,
    onSuccess: (_, trip_id) => { toast.success(trip_id ? "Ganado y viaje asociado" : "Marcado como ganado"); qc.invalidateQueries({ queryKey: ["pipeline-ops"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pipeline-win-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="h-4 w-4 text-emerald-600" />Marcar como Ganado</DialogTitle>
          <DialogDescription className="text-xs">¿Has encontrado el viaje de <strong>{op.client_name}</strong>? Asócialo para vincular la ficha de cliente (sin duplicar).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar viaje por nombre del cliente…" className="pl-8" data-testid="pipeline-win-search" />
          </div>
          <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="pipeline-win-trips">
            {tripsQ.isLoading ? <Skeleton className="h-20 w-full" /> : trips.length === 0 ? (
              <p className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground" data-testid="pipeline-win-empty">Sin viajes que coincidan. Puedes marcar ganado sin asociar.</p>
            ) : trips.map((t) => (
              <button key={t.id} type="button" onClick={() => setSel(t.id === sel ? null : t.id)}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${sel === t.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
                data-testid={`pipeline-win-trip-${t.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium">{t.main_traveler}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{t.destination || "—"} · {fmtDate(t.start_date)} · {t.agent || "—"}</p>
                </div>
                {sel === t.id ? <Link2 className="h-4 w-4 shrink-0 text-primary" /> : <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => winMut.mutate(null)} disabled={winMut.isPending} data-testid="pipeline-win-noassoc"><Unlink className="mr-1.5 h-4 w-4" />Ganado sin asociar</Button>
          <Button onClick={() => winMut.mutate(sel)} disabled={!sel || winMut.isPending} data-testid="pipeline-win-confirm">
            {winMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trophy className="mr-1.5 h-4 w-4" />}Ganar y asociar viaje
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Tablero =============================
function Board({ isAdmin, agents, labels }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [winOp, setWinOp] = useState(null);
  const [remindOp, setRemindOp] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const opsQ = useQuery({ queryKey: ["pipeline-ops"], queryFn: async () => (await api.get("/pipeline/opportunities")).data });
  const ops = opsQ.data?.items || [];
  const grouped = useMemo(() => {
    const g = { interesado: [], posible_cierre: [], ganado: [], perdido: [] };
    ops.forEach((o) => { if (g[o.status]) g[o.status].push(o); });
    return g;
  }, [ops]);

  const moveMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/pipeline/opportunities/${id}/status`, { status })).data,
    onSuccess: (_, { status }) => { toast.success(`Movido a ${labels[status]}`); qc.invalidateQueries({ queryKey: ["pipeline-ops"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const handleDrop = (status) => {
    setOverCol(null);
    const op = ops.find((o) => o.id === dragId);
    setDragId(null);
    if (!op || op.status === status) return;
    if (status === "ganado") { setWinOp(op); return; }  // ganar pide asociar viaje
    moveMut.mutate({ id: op.id, status });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{isAdmin ? "Pipeline completo del equipo. Arrastra las tarjetas entre columnas." : "Tu pipeline personal. Arrastra las tarjetas entre columnas para cambiar de estado."}</p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="pipeline-new-btn"><Plus className="mr-1.5 h-4 w-4" />Nueva oportunidad</Button>
      </div>

      {opsQ.isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="pipeline-board">
          {COLS.map((status) => {
            const list = grouped[status];
            const a = ACCENT[status];
            // Suma total del columna (importe agregado) — se muestra siempre
            // para admins/managers. Los agentes no admin también la ven para
            // sus propias opps: si el kanban tiene filtros aplicados, la suma
            // refleja solo lo visible en esa columna, así siempre cuadra.
            const colTotal = list.reduce((s, op) => s + (Number(op.amount) || 0), 0);
            return (
              <section key={status}
                onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCol(status); } }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setOverCol(null); }}
                onDrop={() => handleDrop(status)}
                className={`rounded-xl border bg-muted/20 p-3 transition-colors ${overCol === status ? "border-primary bg-primary/5 ring-2 ring-primary/30" : ""}`}
                data-testid={`pipeline-col-${status}`}>
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${a.dot}`} />
                      <h3 className="text-xs font-semibold uppercase tracking-wide">{labels[status]}</h3>
                      <span className="text-xs text-muted-foreground" data-testid={`pipeline-col-count-${status}`}>({list.length})</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className={`mt-1 text-lg font-bold tabular-nums ${a.text}`} data-testid={`pipeline-col-total-${status}`}>
                      {fmtEUR(colTotal)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2.5">
                  {list.length === 0 && <div className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground">{overCol === status ? "Suelta aquí" : "Sin oportunidades."}</div>}
                  {list.map((op) => <OpportunityCard key={op.id} op={op} isAdmin={isAdmin} labels={labels} onEdit={(o) => { setEditing(o); setDialogOpen(true); }} onWin={setWinOp} onRemind={setRemindOp} onDragStart={() => setDragId(op.id)} onDragEnd={() => { setDragId(null); setOverCol(null); }} isDragging={dragId === op.id} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <OpportunityDialog open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} isAdmin={isAdmin} agents={agents} labels={labels} />
      {winOp && <WinDialog op={winOp} onClose={() => setWinOp(null)} />}
      <ReminderDialog open={!!remindOp} onClose={() => setRemindOp(null)} entity={remindOp ? { type: "opportunity", id: remindOp.id, name: remindOp.client_name, next_action_at: remindOp.next_action_at, next_action_note: remindOp.next_action_note } : null} />
    </div>
  );
}

// ============================= Dashboard =============================
const Stat = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3" data-testid={`pipeline-stat-${label}`}>
    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></div>
    <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="text-lg font-bold tabular-nums leading-none">{value}</p>{sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}</div>
  </div>
);

function Dashboard({ agents, labels, isDirectorSales }) {
  const [agentId, setAgentId] = useState("all");
  const statsQ = useQuery({
    queryKey: ["pipeline-stats", agentId],
    queryFn: async () => (await api.get("/pipeline/stats", { params: agentId !== "all" ? { agent_id: agentId } : {} })).data,
  });
  const d = statsQ.data;
  const bs = d?.by_status || {};

  return (
    <div className="space-y-4">
      <Card><CardContent className="flex flex-wrap items-center gap-3 p-4">
        <Label className="text-sm">Agente</Label>
        <Select value={agentId} onValueChange={setAgentId}>
          <SelectTrigger className="h-9 w-56" data-testid="pipeline-stats-agent"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo el equipo</SelectItem>
            {(agents || []).map((ag) => <SelectItem key={ag.id} value={ag.id}>{ag.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      {statsQ.isLoading ? <Skeleton className="h-64 w-full" /> : (
        <>
          <div className={`grid grid-cols-2 gap-3 ${isDirectorSales ? "lg:grid-cols-2" : "lg:grid-cols-4"}`}>
            <Stat icon={TrendingUp} label={labels.interesado} value={fmtEUR(bs.interesado?.total || 0)} sub={`${bs.interesado?.count || 0} oport.`} tone="bg-sky-100 text-sky-700" />
            <Stat icon={TrendingUp} label={labels.posible_cierre} value={fmtEUR(bs.posible_cierre?.total || 0)} sub={`${bs.posible_cierre?.count || 0} oport.`} tone="bg-amber-100 text-amber-700" />
            {!isDirectorSales && <Stat icon={Trophy} label={labels.ganado} value={fmtEUR(bs.ganado?.total || 0)} sub={`${bs.ganado?.count || 0} oport.`} tone="bg-emerald-100 text-emerald-700" />}
            {!isDirectorSales && <Stat icon={X} label={labels.perdido} value={fmtEUR(bs.perdido?.total || 0)} sub={`${bs.perdido?.count || 0} oport.`} tone="bg-red-100 text-red-700" />}
          </div>

          <div className="overflow-x-auto rounded-xl border" data-testid="pipeline-by-agent">
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr><th className="px-4 py-3">Agente</th><th className="px-3 py-3 text-right">{labels.interesado}</th><th className="px-3 py-3 text-right">{labels.posible_cierre}</th>{!isDirectorSales && <th className="px-3 py-3 text-right">{labels.ganado}</th>}{!isDirectorSales && <th className="px-3 py-3 text-right">{labels.perdido}</th>}</tr>
              </thead>
              <tbody>
                {(d?.by_agent || []).map((a) => (
                  <tr key={a.owner_id} className="border-t hover:bg-zinc-50/50" data-testid={`pipeline-agent-${a.owner_id}`}>
                    <td className="px-4 py-2 font-medium">{a.owner_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(a.interesado)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(a.posible_cierre)}</td>
                    {!isDirectorSales && <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtEUR(a.ganado)}</td>}
                    {!isDirectorSales && <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtEUR(a.perdido)}</td>}
                  </tr>
                ))}
                {(d?.by_agent || []).length === 0 && <tr><td colSpan={isDirectorSales ? 3 : 5} className="py-8 text-center text-sm text-muted-foreground">Sin datos.</td></tr>}
              </tbody>
            </table>
          </div>

          {(d?.by_month || []).length > 0 && (
            <div className="overflow-x-auto rounded-xl border" data-testid="pipeline-by-month">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr><th className="px-4 py-3">Mes de llegada</th>{!isDirectorSales && <th className="px-3 py-3 text-right">{labels.ganado}</th>}<th className="px-3 py-3 text-right">{labels.posible_cierre}</th><th className="px-3 py-3 text-right">{labels.interesado}</th></tr>
                </thead>
                <tbody>
                  {(d.by_month).map((m) => (
                    <tr key={m.month} className="border-t hover:bg-zinc-50/50">
                      <td className="px-4 py-2 font-medium">{m.month}</td>
                      {!isDirectorSales && <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtEUR(m.ganado)}</td>}
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(m.posible_cierre)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(m.interesado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================= Sync app externa =============================
function ExternalSyncPanel() {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const statusQ = useQuery({
    queryKey: ["pipeline-ext-status"],
    queryFn: async () => (await api.get("/pipeline/sync-external/status")).data,
    refetchInterval: 60_000,
  });
  const s = statusQ.data;

  const syncMut = useMutation({
    mutationFn: async () => (await api.post("/pipeline/sync-external", {})).data,
    onSuccess: (r) => {
      const c = r.created || 0;
      const u = r.updated || 0;
      const uc = r.unchanged || 0;
      toast.success(`Sync OK · ${c} nuevas · ${u} actualizadas · ${uc} sin cambios`);
      qc.invalidateQueries({ queryKey: ["pipeline-ops"] });
      qc.invalidateQueries({ queryKey: ["pipeline-ext-status"] });
      qc.invalidateQueries({ queryKey: ["pipeline-stats"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggleMut = useMutation({
    mutationFn: async (enabled) => (await api.post("/pipeline/sync-external/toggle", { enabled })).data,
    onSuccess: (_, enabled) => {
      toast.success(enabled
        ? "Sync externa activada. SOFI sigue actualizando desde la app externa."
        : "Sync externa desactivada. A partir de ahora SOFI es la fuente de verdad.");
      qc.invalidateQueries({ queryKey: ["pipeline-ext-status"] });
      setConfirmOpen(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (!s) return null;
  const last = s.last_sync;
  const lastTs = last?.ts ? new Date(last.ts) : null;
  const lastAgo = lastTs
    ? (() => {
        const mins = Math.floor((Date.now() - lastTs.getTime()) / 60000);
        if (mins < 1) return "hace segundos";
        if (mins < 60) return `hace ${mins} min`;
        const h = Math.floor(mins / 60);
        if (h < 24) return `hace ${h} h`;
        return `hace ${Math.floor(h / 24)} d`;
      })()
    : "nunca";

  return (
    <>
      <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5 text-xs shadow-sm"
           data-testid="pipeline-ext-panel">
        <Cable className={`h-3.5 w-3.5 ${s.enabled ? "text-emerald-600" : "text-zinc-400"}`} />
        <div className="hidden sm:block">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold">App externa</span>
            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${s.enabled ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-300 bg-zinc-50 text-zinc-600"}`}>
              {s.enabled ? "conectada" : "desconectada"}
            </Badge>
          </div>
          <div className="text-[10px] text-muted-foreground">
            {s.ops_external} ops · última sync {lastAgo}
          </div>
          <AutomationStoppedPill className="mt-0.5 !px-1.5 !py-0 !text-[9px]" testid="pipeline-automation-stopped" />
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending || !s.enabled}
          data-testid="pipeline-ext-sync-btn"
          title={s.enabled ? "Sincronizar ahora" : "Sync desactivada"}
        >
          {syncMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={() => setConfirmOpen(true)}
          data-testid="pipeline-ext-toggle-btn"
          title={s.enabled ? "Desconectar app externa" : "Reconectar app externa"}
        >
          {s.enabled ? <PowerOff className="h-3.5 w-3.5 text-red-500" /> : <Power className="h-3.5 w-3.5 text-emerald-600" />}
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent data-testid="pipeline-ext-toggle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {s.enabled ? <><PowerOff className="h-4 w-4 text-red-500" />Desconectar app externa</> : <><Power className="h-4 w-4 text-emerald-600" />Reconectar app externa</>}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {s.enabled
                ? "A partir de ahora SOFI dejará de sincronizar con pipeline.espiritutravel.com. Los agentes crearán/editarán oportunidades solo en SOFI. Puedes reconectar más adelante si lo necesitas."
                : "Reactivar la sync diaria y traer los cambios pendientes desde pipeline.espiritutravel.com."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button
              variant={s.enabled ? "destructive" : "default"}
              onClick={() => toggleMut.mutate(!s.enabled)}
              disabled={toggleMut.isPending}
              data-testid="pipeline-ext-toggle-confirm"
            >
              {toggleMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {s.enabled ? "Sí, desconectar" : "Sí, reconectar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================= Página =============================
export default function PipelinePage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const { pipeline: labels } = useCrmLabels();
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [gcfgOpen, setGcfgOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const g = params.get("google");
    if (g === "connected") toast.success("Google Calendar conectado correctamente");
    else if (g === "error") toast.error("No se pudo conectar Google Calendar. Revisa la configuración.");
    if (g) { params.delete("google"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const agentsQ = useQuery({
    queryKey: ["pipeline-agents"],
    queryFn: async () => (await api.get("/pipeline/agents")).data,
    enabled: isAdmin,
  });
  const agents = agentsQ.data?.items || [];

  return (
    <div className="space-y-5" data-testid="pipeline-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><KanbanSquare className="h-6 w-6 text-primary" />Pipeline & Cualificación</h1>
          <p className="text-sm text-muted-foreground">Primero cualifica los leads entrantes (web/Maya); los que avanzan pasan al pipeline de ventas por agente.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <ExternalSyncPanel />}
          <GcalConnect />
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="pipeline-settings-btn" aria-label="Configuración"><Settings className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]">
                <DropdownMenuItem onClick={() => setLabelsOpen(true)} data-testid="open-stage-labels"><Settings className="mr-2 h-3.5 w-3.5" />Configurar etapas</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setGcfgOpen(true)} data-testid="open-gcal-config"><Calendar className="mr-2 h-3.5 w-3.5" />Configurar Google Calendar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Tabs defaultValue="cualificacion" className="w-full">
        <TabsList data-testid="pipeline-tabs">
          <TabsTrigger value="cualificacion" data-testid="pipeline-tab-cualificacion"><Sparkles className="mr-1.5 h-4 w-4" />Cualificación</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="pipeline-tab-pipeline"><KanbanSquare className="mr-1.5 h-4 w-4" />Pipeline</TabsTrigger>
          {isAdmin && <TabsTrigger value="dashboard" data-testid="pipeline-tab-dashboard"><BarChart3 className="mr-1.5 h-4 w-4" />Dashboard</TabsTrigger>}
        </TabsList>
        <TabsContent value="cualificacion" className="mt-4"><CualificacionBoard /></TabsContent>
        <TabsContent value="pipeline" className="mt-4"><Board isAdmin={isAdmin} agents={agents} labels={labels} /></TabsContent>
        {isAdmin && <TabsContent value="dashboard" className="mt-4"><Dashboard agents={agents} labels={labels} isDirectorSales={user?.role === "director_sales"} /></TabsContent>}
      </Tabs>

      <StageLabelsDialog open={labelsOpen} onClose={() => setLabelsOpen(false)} />
      <GoogleConfigDialog open={gcfgOpen} onClose={() => setGcfgOpen(false)} />
    </div>
  );
}
